<template>
  <Card containerClass="mb-5 shadow-sm" titleContainerClass="d-flex justify-content-center">
    <template #cardHeader>
      <div class="card-header">
        <h3 class="card-title align-items-start flex-column">
          {{$t('absences.setting.public_holidays')}}
        </h3>
        <div class="card-toolbar">
          <div class="d-flex">
            <YearPicker id="kt_datepicker_year" :startDate="query.start_date" @update:date="handleRangeDate"/>
            <button @click="openCreateModal" class="btn btn-primary ms-5" type="button">
              {{ $t('button.create') }}
            </button>
          </div>
        </div>
      </div>
    </template>
    <template #cardBody>
      <table class="table align-middle table-row-bordered fs-6 gy-3 gx-4" id="kt_datatable_public_holidays" style="margin-top: 13px !important; cursor:pointer">
        <thead>
          <tr class="fw-bold fs-6" style="background-color: #F2F3F4;">
            <th class="min-w-200px">{{$t('absences.setting.name')}}</th>
            <th class="min-w-100px">{{$t('absences.setting.start_date')}}</th>
            <th class="min-w-100px">{{$t('absences.setting.end_date')}}</th>
            <th class="min-w-100px">{{$t('absences.setting.description')}}</th>
            <th class="min-w-50px">{{ $t('absences.actions') }}</th>
          </tr>
        </thead>
      </table>

      <CreatePublicHolidayModal id="public_holiday_create" :form="formCreateData" @load="loadData"/>
      <CreatePublicHolidayModal id="public_holiday_edit" :edit="true" :form="formEditData" @load="loadData"/>
    </template>
  </Card>
</template>

<script setup>
import Card from "@/Modules/Financial/components/Card.vue";
import {computed, nextTick, onMounted, ref, watch, defineProps} from "vue";
import CreatePublicHolidayModal from "@/Modules/RH/views/Setting/components/Modals/CreatePublicHolidayModal.vue";
import {useStore} from "vuex";
import i18n from "@/plugins/i18n";
import Ls from "@/services/ls";
import {BASE_URL} from "@/services/api";
import {useLocales} from "@/composables/locales";
import {useHasAbility} from "@/composables/hasAbility";
import abilities from "@/composables/abilities";
import {Theme} from "@/composables/theming";
import {useSwal} from "@/composables/useSwal";
import { useI18n } from 'vue-i18n'       
import YearPicker from "@/components/YearPicker";

const store = useStore()
const { can } = useHasAbility()
const {showSwal} = useSwal()
const { t } = useI18n({ useScope: 'global' })

const props = defineProps({
  tab: {
    type: String,
    required: true
  }
})

const {locale, datatableLocaleUrl} = useLocales()

const table = ref(null)
const formCreateData = ref({
  name: '',
  start_date: '',
  end_date: '',
  description: '',
  interval: false,
  constant: false,
  country_id: ''
})
const formEditData = ref({
  id: '',
  name: '',
  start_date: '',
  end_date: '',
  description: '',
  interval: false,
  constant: false,
  country_id: ''
})

const query= ref({
  start_date: moment().format("DD-MM-YYYY"),
  end_date: moment().format("DD-MM-YYYY"),
})

const currentSociety = computed(() => store.getters['offices/getSelectedCompany'])

watch(() => currentSociety.value, (value) => {
  if(value){
    formCreateData.value.country_id = value?.country_id
    formEditData.value.country_id = value?.country_id
    fetchDatas()
  }
}, { deep: true })

watch(() => query.value.start_date, (value) => {
  if(value){
    loadData()
  }
})

watch(() => locale.value, (value) => {
  loadData()
}, { deep: true })

function loadData() {
  if(currentSociety.value){
    table.value?.ajax?.url(`${BASE_URL}/get/office/${currentSociety.value?.id}/public/holiday/datatables?society_id=${currentSociety.value?.id}&startDate=${query.value.start_date}&endDate=${query.value.end_date}`).load()
  }
}

function fetchDatas() {
  const AUTH_TOKEN = Ls.get('auth.token')
  const pageLimit = Ls.get('pageLength')

  table.value = $("#kt_datatable_public_holidays").DataTable({
    destroy: true,
    responsive: !0,
    searchDelay: 500,
    processing: !0,
    serverSide: !0,
    pageLength: pageLimit ||  5,
    lengthMenu: [5, 10, 25, 50, 75, 100 ],
    order: [[ 0, 'desc']],
    ajax: {
      url: BASE_URL + `/get/office/${currentSociety.value?.id}/public/holiday/datatables?society_id=${currentSociety.value?.id}&startDate=${query.value.start_date}&endDate=${query.value.end_date}`,
      type: "GET",
      beforeSend: function (request) {
        request.setRequestHeader("Authorization", "Bearer " + AUTH_TOKEN);
        request.setRequestHeader("Company", Ls.get('current.company'));
      }
    },
    columns: [
    { data: "name" }, { data: "start_date" }, { data: "end_date" }, { data: "description" }, { data: "Actions", responsivePriority: -1 }
    ],
    columnDefs: [
      {
        targets: -5,
        order: "desc",
        className: "dt-center text-center clickable" ,
        render: function (t, a, e, l) {
          return '<span data-id="'+ e.id +'">' + t + "</span>";
        },
      },
      {
        targets: -4,
        order: "desc",
        className: "dt-center text-center clickable" ,
        render: function (t, a, e, l) {
          return '<span data-id="'+ e.id +'">' + t + "</span>";
        },
      },
      {
        targets: -3,
        order: "desc",
        className: "dt-center text-center clickable" ,
        render: function (t, a, e, l) {
          return '<span data-id="'+ e.id +'">' + t + "</span>";
        },
      },
      {
        targets: -2,
        orderable: false,
        className: "dt-center text-center" ,
        render: function (t, a, e, l) {
          return '<span data-id="'+ e.id +'">' + t + "</span>";
        },
      },
      {
        width: "10%",
        targets: -1,
        "className": "dt-center text-start",
        title: "Actions",
        orderable: !1,
        render: (t, a, e, l) => {
          let render=""
          render+='<a href="javascript:;" data-id="'+ e.id +'"  data-name="'+ e.name +'" data-description="'+ e.description +'" data-start_date="'+ e.start_date +'" data-end_date="'+ e.end_date +'" class="btn btn-sm btn-clean btn-icon edit_btn" title="'+ i18n.global.t('button.edit') +'"><i data-id="'+ e.id +'"  data-name="'+ e.name +'" data-description="'+ e.description +'" data-start_date="'+ e.start_date +'" data-end_date="'+ e.end_date +'" class="fas fa-edit fs-2" style="color: '+Theme.gray +'"></i></a>'
          render+='<a href="javascript:;" data-id="'+ e.id +'" class="btn btn-sm btn-clean btn-icon delete_btn" title="'+ i18n.global.t('button.delete') +'"><i  data-id="'+ e.id +'" class="fas fa-trash fs-2" style="color: '+Theme.gray +'"></i></a>'
          return render
        },
      },

    ],
    language: {
      url: datatableLocaleUrl.value,
    }
  });

  $(document).on("click", "#kt_datatable_public_holidays .edit_btn", async(e) => {
    formEditData.value.id = e.target.getAttribute('data-id')
    formEditData.value.name = e.target.getAttribute('data-name')
    formEditData.value.description = e.target.getAttribute('data-description')
    formEditData.value.start_date = e.target.getAttribute('data-start_date')
    formEditData.value.end_date = e.target.getAttribute('data-end_date')

    $('#public_holiday_edit').modal('toggle')
  });

  $(document).on("click", "#kt_datatable_public_holidays .delete_btn", async(e) => {
    let id = e.target.getAttribute('data-id')
    await deletePublicHolidays(id)
  });
}

function deletePublicHolidays(id) {
  showSwal(
      t('absences.swal.title'),
      t('absences.swal.public_holidays_deleting')
  ).then( async (result) => {
    if (result.value) {
      try {
        store.dispatch('holydays/deletePublicHoliday', id)
          .then(() => {
              loadData()
              window.toastr['success'](i18n.global.t('absences.setting.public_holidays_deleted_successfully'))
          })
      } catch (e) {
      }
    }
  });
}

function openCreateModal(){
  $('#public_holiday_create').modal('toggle')
}

function handleRangeDate (range) {
  query.value.start_date = range.start
  query.value.end_date = range.end
}

onMounted(() => {
  store.dispatch('offices/loadOfficesBySocieties') 
  if(currentSociety.value && query.value.start && query.value.end_date){
    fetchDatas()
  }
})
</script>

<style scoped>

  .end-3 {
    right: 1rem;
  }

</style>